import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  TableContainer,
  TableRow,
  TableBody,
  TableCell,
  Table,
  TableHead,
  Paper,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import {
  setReturnedOrder,
  addSkuCorrection,
  clearSkuCorrection,
} from '../../store/slices/orderSlice';
import SkuCorrection from '../SkuCorrectionModal/SkuCorrection';
import QuantitySelector from '../../components/QuantitySelector/QuantitySelector';

const HighlightColor = {
  Complete: '#d6f5d6',
  Partial: '#f5eeb8',
  None: 'white',
};

export default function ReturnProductModal({
  isOpen,
  handleClose,
  submitChanges,
}) {
  const dispatch = useDispatch();
  const selectedReturnOrder = useSelector(
    (state) => state.order.selectedReturnOrder
  );
  const currentPrinter = useSelector((state) => state.order.currentPrinter);
  const { OrderNumber, ReturnLines } = selectedReturnOrder;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [updatedQuantities, setUpdatedQuantities] = useState({});
  const [skuModalOpen, setSkuModalOpen] = useState(false);
  const [skuToChange, setSkuToChange] = useState('');
  const [skuQtyToChange, setSkuQtyToChange] = useState('');
  const [rowToChange, setRowToChange] = useState(undefined);
  const [changedSkus, setChangedSkus] = useState([]);

  const handleOpenSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSkuModalOpen = (sku, qty, row) => {
    setSkuToChange(sku);
    setSkuQtyToChange(qty);
    setRowToChange(row);
    setSkuModalOpen(true);
  };

  const handleSkuModalClose = () => {
    setSkuToChange('');
    setSkuQtyToChange(0);
    setRowToChange(undefined);
    setSkuModalOpen(false);
  };

  const handleSkuCorrection = (skuCorrection) => {
    setUpdatedQuantities((prev) => {
      const minQtyAllowed = skuCorrection.quantity || 0;

      const newQty = Math.max((prev[rowToChange] || 0) - 1, minQtyAllowed);
      return {
        ...prev,
        [rowToChange]: newQty,
      };
    });

    setChangedSkus((prev) => {
      // Remove any existing correction for this row first
      const filteredSkus = prev.filter(
        (changedSku) => changedSku.index !== rowToChange
      );

      // Add the new correction
      return [
        ...filteredSkus,
        {
          sku: skuCorrection.sku,
          quantity: skuCorrection.quantity,
          index: rowToChange,
          color: skuCorrection.color,
          theme: skuCorrection.theme,
          description: skuCorrection.description,
          vendor: skuCorrection.vendor,
        },
      ];
    });

    dispatch(addSkuCorrection({ ...skuCorrection, index: rowToChange }));
    setSkuModalOpen(false);
  };

  const handleSubmit = () => {
    // Find all rows with non-zero quantity
    const selectedReturnLines = selectedReturnOrder.ReturnLines.map(
      (line, index) => {
        const qty = parseInt(updatedQuantities[index], 10);
        return {
          ...line,
          ReturnQuantity: !isNaN(qty) ? qty : line.ReturnQuantity,
        };
      }
    ).filter((line) => line.ReturnQuantity && line.ReturnQuantity > 0);

    if (selectedReturnLines.length === 0) {
      handleOpenSnackbar('Please set a return quantity for at least one item');
      return;
    }

    const hasInvalidQty = selectedReturnLines.some(
      (line) => isNaN(line.ReturnQuantity) || line.ReturnQuantity <= 0
    );
    if (hasInvalidQty) {
      handleOpenSnackbar('Invalid quantity for one or more selected items');
      return;
    }

    // Update the order with new quantities
    let updatedOrder = JSON.parse(JSON.stringify(selectedReturnOrder));
    updatedOrder.ReturnLines.forEach((line, index) => {
      const qty = parseInt(updatedQuantities[index], 10);
      if (!isNaN(qty)) {
        line.ReturnQuantity = qty;
      }
    });

    dispatch(setReturnedOrder(updatedOrder));
    if (!currentPrinter) {
      handleOpenSnackbar('Please select a printer');
    } else {
      submitChanges(selectedReturnLines, OrderNumber, currentPrinter);
    }
  };

  const decrementQty = (index) => {
    setUpdatedQuantities((prev) => {
      const newQty = Math.max((prev[index] || 0) - 1, 0);

      if (newQty === 0) {
        setChangedSkus((prevChangedSkus) =>
          prevChangedSkus.filter((changedSku) => changedSku.index !== index)
        );
        dispatch(clearSkuCorrection({ index }));
      } else {
        // Check if there's an existing SKU correction for this line
        const existingCorrection = changedSkus.find(
          (changedSku) => changedSku.index === index
        );

        if (existingCorrection && newQty < existingCorrection.quantity) {
          // Update the existing correction quantity to match the new decremented value
          setChangedSkus((prevChangedSkus) =>
            prevChangedSkus.map((changedSku) =>
              changedSku.index === index
                ? { ...changedSku, quantity: newQty }
                : changedSku
            )
          );

          // Update the Redux store with the new correction quantity
          dispatch(
            addSkuCorrection({
              ...existingCorrection,
              quantity: newQty,
              index,
            })
          );
        }
      }

      return {
        ...prev,
        [index]: newQty,
      };
    });
  };

  const incrementQty = (index) => {
    setUpdatedQuantities((prev) => ({
      ...prev,
      [index]: Math.min(
        (prev[index] || 0) + 1,
        Math.abs(ReturnLines[index].OrderQuantity)
      ),
    }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      sx={{ marginTop: '3.5%', maxHeight: '92vh' }}
    >
      <DialogTitle component="div">
        <Typography variant="h3" sx={{ marginY: 1 }}>
          Return Product by Order - {OrderNumber}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Quantity Expected</TableCell>
                <TableCell>Sku</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Return Quantity</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ReturnLines.map((line, index) => {
                
                const qty =
                  updatedQuantities[index] !== undefined
                    ? updatedQuantities[index]
                    : line.ReturnQuantity;
                let rowStyle = {};
                if (
                  changedSkus.some((changedSku) => changedSku.index === index)
                ) {
                  rowStyle = { backgroundColor: '#FFEDCC' };
                } else if (qty && qty > 0) {
                  rowStyle = { backgroundColor: 'lightblue' };
                } else  {
                  rowStyle = { backgroundColor: HighlightColor[line.Highlight] };
                }
                return (
                  <TableRow key={index} style={rowStyle}>
                    <TableCell></TableCell>
                    <TableCell sx={{ paddingLeft: '43px', fontSize: '23px' }}>
                      {Math.abs(line.OrderQuantity)}
                      {changedSkus.some(
                        (changedSku) => changedSku.index === index
                      ) && (
                        <Typography
                          variant="body1"
                          sx={{ marginLeft: 0.2, marginTop: -1.2 }}
                        >
                          {
                            changedSkus.find(
                              (changedSku) => changedSku.index === index
                            ).quantity
                          }
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {line.SkuNumber}
                      {changedSkus.some(
                        (changedSku) => changedSku.index === index
                      ) && (
                        <Typography
                          variant="body2"
                          sx={{ borderTop: '1px solid' }}
                        >
                          {
                            changedSkus.find(
                              (changedSku) => changedSku.index === index
                            ).sku
                          }
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {line.SkuDescription}
                      {changedSkus.some(
                        (changedSku) => changedSku.index === index
                      ) && (
                        <Typography
                          variant="body2"
                          style={{
                            textAlign: 'center',
                            borderTop: '1px solid',
                          }}
                        >
                          {
                            changedSkus.find(
                              (changedSku) => changedSku.index === index
                            ).description
                          }
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <QuantitySelector
                        increment={() => incrementQty(index)}
                        decrement={() => decrementQty(index)}
                        disabled={false}
                      >
                        {updatedQuantities[index] !== undefined
                          ? updatedQuantities[index]
                          : line.ReturnQuantity}
                      </QuantitySelector>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() =>
                          handleSkuModalOpen(
                            line.SkuNumber,
                            Math.abs(line.OrderQuantity),
                            index
                          )
                        }
                        disabled={false}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} variant="contained" color="success">
          Update
        </Button>
        <Button onClick={handleClose} variant="outlined" color="error">
          Cancel
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {skuModalOpen ? (
        <SkuCorrection
          isOpen={skuModalOpen}
          handleClose={handleSkuModalClose}
          onSubmit={handleSkuCorrection}
          sku={skuToChange}
          skuQty={skuQtyToChange}
          existingCorrection={
            rowToChange !== undefined &&
            selectedReturnOrder.ReturnLines[rowToChange]?.SkuCorrection
              ? {
                  sku: selectedReturnOrder.ReturnLines[rowToChange]
                    .SkuCorrection,
                  quantity:
                    selectedReturnOrder.ReturnLines[rowToChange]
                      .SkuCorrectionQuantity || 0,
                  color:
                    changedSkus.find((cs) => cs.index === rowToChange)?.color ||
                    '',
                  theme:
                    changedSkus.find((cs) => cs.index === rowToChange)?.theme ||
                    '',
                  description:
                    changedSkus.find((cs) => cs.index === rowToChange)
                      ?.description || '',
                  vendor:
                    changedSkus.find((cs) => cs.index === rowToChange)
                      ?.vendor || '',
                }
              : null
          }
        />
      ) : null}
    </Dialog>
  );
}
